import { CompleteProfileUpdateRequest } from '@api/dto';
import { TextFieldControl } from '@components/TextFieldControl';
import { EXAMPLE_VALID_PHONE_NUMBER } from '@constants';
import useAuth from '@contexts/AuthProvider';
import { useRegisterContext } from '@contexts/RegisterProvider';
import { Button, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { useGetEmployeeQuery } from '../hooks/useGetEmployeeQuery';

interface Props {
  employeeId: string;
  error: string | null;
  fieldError: string | null;
  showLoadingButton: boolean;
  onSubmit: (data: CompleteProfileUpdateRequest) => Promise<void>;
}

export default function OnePortalForm({ employeeId, error, fieldError, showLoadingButton, onSubmit }: Props) {
  const { t } = useTranslation();
  const { company } = useRegisterContext();
  const { tokenParsed } = useAuth();
  const { data: employeeData, isSuccess: isGetEmployeeQuerySuccess } = useGetEmployeeQuery(employeeId);

  const { control, handleSubmit, setError, reset } = useForm<CompleteProfileUpdateRequest>({
    defaultValues: {
      firstName: '',
      lastName: '',
      workPhoneNumber: '',
    },
  });

  useEffect(() => {
    if (!isGetEmployeeQuerySuccess) return;

    reset(
      {
        firstName: employeeData.firstName || tokenParsed?.given_name,
        lastName: employeeData.lastName || tokenParsed?.family_name,
        workPhoneNumber: employeeData.workPhoneNumber,
      },
      { keepDirtyValues: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeData, isGetEmployeeQuerySuccess, reset]);

  useEffect(() => {
    if (!fieldError) return;
    setError(fieldError as keyof CompleteProfileUpdateRequest, { type: 'required' }, { shouldFocus: true });
  }, [fieldError, setError]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack sx={{ gap: 3 }}>
        <Stack sx={{ gap: 1, width: '100%' }}>
          <Typography variant="h5">
            <Trans i18nKey="registration.completeData" />
          </Typography>
          <Typography>
            <Trans i18nKey="registration.employer" values={{ companyName: company?.name }} />
          </Typography>
        </Stack>
        {error && (
          <Typography color="error">
            <Trans i18nKey={error} />
          </Typography>
        )}
        <Stack sx={{ gap: 2, width: '100%' }}>
          <TextFieldControl
            required
            name="firstName"
            control={control}
            variant="outlined"
            label={t('registration.firstName')}
            autoComplete="given-name"
            rules={{ maxLength: 100 }}
          />
          <TextFieldControl
            required
            name="lastName"
            control={control}
            variant="outlined"
            label={t('registration.lastName')}
            autoComplete="family-name"
            rules={{ maxLength: 100 }}
          />
          <Controller
            name="workPhoneNumber"
            control={control}
            render={({ field }) => (
              <TextFieldControl
                required
                name="workPhoneNumber"
                control={control}
                variant="outlined"
                label={t('registration.phoneNumber')}
                autoComplete="tel"
                type="tel"
                rules={{
                  pattern: {
                    value: /^(?:\+[1-9]\d{0,2}|0)\d{6,14}$/,
                    message: t('registration.phoneNumberInvalid', {
                      EXAMPLE_VALID_PHONE_NUMBER: EXAMPLE_VALID_PHONE_NUMBER(),
                    }),
                  },
                }}
                onChange={e => field.onChange(e.target.value.replace(/\s+/g, ''))}
              />
            )}
          />
        </Stack>
        <Button type="submit" loading={showLoadingButton}>
          <Trans i18nKey="registration.actions.finish" />
        </Button>
      </Stack>
    </form>
  );
}
